/**
 * A business's branding: its name, logo, tagline and colour, shown on its kiosk screens, the customer pages opened
 * from the QR and its portal, so they look like the business's own.
 */
export const DEFAULT_TAGLINE = 'Self-service printing';
export const BRAND_NAME_MAX = 120;
export const BRAND_TAGLINE_MAX = 80;
/** Largest logo an owner may upload; it is resized to 512 px and stored as WebP. */
export const LOGO_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp';
/** Suggested colours on the settings page. */
export const BRAND_COLOR_PRESETS = ['#4f46e5', '#2563eb', '#0891b2', '#059669', '#65a30d', '#d97706', '#dc2626', '#db2777', '#7c3aed', '#0f172a'];
/** The product, named in small print ("Powered by …") on businesses' customer pages. */
export const PRODUCT_NAME = 'ParthPrint';
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
export function isHexColor(v) {
    return HEX_RE.test(v.trim());
}
/** "#ABC" / "abc" / "#aabbcc" → "#aabbcc"; null when it isn't a colour. */
export function normalizeHex(v) {
    const m = HEX_RE.exec(v.trim());
    if (!m)
        return null;
    const h = m[1].toLowerCase();
    return `#${h.length === 3 ? [...h].map((c) => c + c).join('') : h}`;
}
function rgbOf(hex) {
    const h = normalizeHex(hex) ?? '#000000';
    return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
}
const toHex = (rgb) => `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
/** `a` mixed with `b`: t = 0 → a, t = 1 → b (sRGB). */
export function mixHex(a, b, t) {
    const x = rgbOf(a);
    const y = rgbOf(b);
    return toHex([0, 1, 2].map((i) => x[i] + (y[i] - x[i]) * t));
}
/** WCAG relative luminance. */
export function luminance(hex) {
    const [r, g, b] = rgbOf(hex).map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a, b) {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
/** Black or white text, whichever reads better on `bg`. */
export function readableTextOn(bg) {
    return contrastRatio(bg, '#ffffff') >= contrastRatio(bg, '#0b1220') ? '#ffffff' : '#0b1220';
}
/**
 * The brand colour nudged (lighter on dark backgrounds, darker on light ones) until it has at least `min` contrast
 * with `background`, so a navy logo colour still reads on the dark kiosk screen and a yellow one on a white page.
 */
export function ensureContrast(color, background, min = 4.5) {
    const towards = luminance(background) < 0.5 ? '#ffffff' : '#000000';
    let c = normalizeHex(color) ?? towards;
    for (let i = 1; i <= 20 && contrastRatio(c, background) < min; i++)
        c = mixHex(color, towards, i * 0.05);
    return c;
}
/** "Shree Ganesh Xerox" → "SG"; "Xerox" → "XE". */
export function brandInitials(name) {
    const words = name
        .replace(/[^\p{L}\p{M}\p{N}\s]/gu, ' ')
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0)
        return '?';
    if (words.length === 1)
        return [...words[0]].slice(0, 2).join('').toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}
/**
 * Tailwind-style shades (50–950) around a brand colour for light pages: 600 is the colour itself, darkened if
 * needed so white text on it stays readable.
 */
export function brandPalette(color) {
    const base = ensureContrast(color, '#ffffff', 4.5);
    return {
        50: mixHex(base, '#ffffff', 0.93),
        100: mixHex(base, '#ffffff', 0.86),
        200: mixHex(base, '#ffffff', 0.74),
        300: mixHex(base, '#ffffff', 0.56),
        400: mixHex(base, '#ffffff', 0.32),
        500: mixHex(base, '#ffffff', 0.14),
        600: base,
        700: mixHex(base, '#000000', 0.18),
        800: mixHex(base, '#000000', 0.34),
        900: mixHex(base, '#000000', 0.5),
        950: mixHex(base, '#000000', 0.66),
    };
}
//# sourceMappingURL=branding.js.map